const PRODUCT_SELECT_EVENT = 'shopify:product:select';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class ReelVideoCarousel extends HTMLElement {
  #activeIndex = 0;
  #modalIndex = 0;
  #timer;
  #scrollTimer;
  #inViewport = false;
  #interacting = false;
  #muted = true;
  #touchStartY = 0;
  #previousFocus;
  #observer;
  #resizeObserver;
  #hasOverflow = false;
  #abortController = new AbortController();

  connectedCallback() {
    this.track = this.querySelector('[data-reel-track]');
    this.cards = Array.from(this.querySelectorAll('[data-reel-card]'));
    this.dialog = this.querySelector('[data-reel-dialog]');
    this.modalVideo = this.querySelector('[data-reel-modal-video]');
    this.modalProduct = this.querySelector('[data-reel-modal-product]');
    this.soundButton = this.querySelector('[data-reel-sound]');
    this.controls = this.querySelector('.reels-carousel__controls');

    if (!this.track || !this.dialog || this.cards.length === 0) return;

    const signal = this.#abortController.signal;
    this.querySelector('[data-reel-previous]')?.addEventListener('click', () => this.#select(this.#activeIndex - 1), {
      signal,
    });
    this.querySelector('[data-reel-next]')?.addEventListener('click', () => this.#select(this.#activeIndex + 1), {
      signal,
    });
    this.querySelector('[data-modal-previous]')?.addEventListener('click', () => this.#showModalReel(this.#modalIndex - 1), {
      signal,
    });
    this.querySelector('[data-modal-next]')?.addEventListener('click', () => this.#showModalReel(this.#modalIndex + 1), {
      signal,
    });
    this.querySelector('[data-reel-close]')?.addEventListener('click', () => this.#closeDialog(), { signal });
    this.soundButton?.addEventListener('click', () => this.#toggleSound(), { signal });

    for (const [index, card] of this.cards.entries()) {
      card.querySelector('[data-reel-open]')?.addEventListener('click', () => this.#openDialog(index), { signal });
    }

    this.track.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    this.track.addEventListener('focusin', () => this.#setInteracting(true), { signal });
    this.track.addEventListener('focusout', () => this.#setInteracting(false), { signal });
    this.track.addEventListener('keydown', this.#handleTrackKeydown, { signal });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#closeDialog();
    }, { signal });
    this.dialog.addEventListener('click', this.#handleDialogClick, { signal });
    this.dialog.addEventListener('touchstart', this.#handleTouchStart, { passive: true, signal });
    this.dialog.addEventListener('touchend', this.#handleTouchEnd, { passive: true, signal });
    this.dialog.addEventListener('click', this.#handleProductPanelClick, { signal });
    document.addEventListener('visibilitychange', this.#syncPlayback, { signal });
    document.addEventListener(PRODUCT_SELECT_EVENT, this.#handleProductSelect, { signal });

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#inViewport = entry.isIntersecting;
        this.#syncPlayback();
      },
      { rootMargin: '100px', threshold: 0.2 }
    );
    this.#observer.observe(this);

    this.#resizeObserver = new ResizeObserver(this.#syncControls);
    this.#resizeObserver.observe(this.track);

    this.#syncControls();
    this.#select(0, false);
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#stopTimer();
    clearTimeout(this.#scrollTimer);
    document.documentElement.classList.remove('reels-dialog-open');
  }

  #handleScroll = () => {
    clearTimeout(this.#scrollTimer);
    this.#scrollTimer = setTimeout(() => {
      const trackRect = this.track.getBoundingClientRect();
      const center = trackRect.left + trackRect.width / 2;
      let closestIndex = this.#activeIndex;
      let closestDistance = Infinity;

      for (const [index, card] of this.cards.entries()) {
        const rect = card.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      }

      if (closestIndex !== this.#activeIndex) this.#select(closestIndex, false);
    }, 100);
  };

  #syncControls = () => {
    const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 2;
    if (this.controls) this.controls.hidden = !hasOverflow;
    if (this.#hasOverflow === hasOverflow) return;
    this.#hasOverflow = hasOverflow;
    this.#syncPlayback();
  };

  #handleTrackKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#select(this.#activeIndex - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#select(this.#activeIndex + 1);
    }
  };

  #handleDialogClick = (event) => {
    if (event.target === this.dialog) this.#closeDialog();
  };

  #handleTouchStart = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-reel-product]')) return;
    this.#touchStartY = event.changedTouches[0]?.clientY ?? 0;
  };

  #handleTouchEnd = (event) => {
    if (!this.#touchStartY) return;
    const endY = event.changedTouches[0]?.clientY ?? this.#touchStartY;
    const distance = this.#touchStartY - endY;
    this.#touchStartY = 0;
    if (Math.abs(distance) < 55) return;
    this.#showModalReel(this.#modalIndex + (distance > 0 ? 1 : -1));
  };

  #handleProductPanelClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const product = target.closest('[data-reel-product]');
    if (!product) return;
    if (target.closest('[data-reel-options-open]')) product.setAttribute('data-options-open', 'true');
    if (target.closest('[data-reel-options-close]')) product.removeAttribute('data-options-open');
    if (target.closest('[data-reel-product-image-previous]')) this.#changeProductImage(product, -1);
    if (target.closest('[data-reel-product-image-next]')) this.#changeProductImage(product, 1);
  };

  #changeProductImage(product, direction) {
    const images = Array.from(product.querySelectorAll('[data-reel-product-image]'));
    if (images.length < 2) return;
    const currentIndex = images.findIndex((image) => !image.hidden);
    const nextIndex = (currentIndex + direction + images.length) % images.length;
    this.#showProductImage(product, nextIndex);
  }

  #showProductImage(product, activeIndex) {
    const images = Array.from(product.querySelectorAll('[data-reel-product-image]'));
    const dots = Array.from(product.querySelectorAll('[data-reel-product-image-dot]'));

    for (const [index, image] of images.entries()) {
      const active = index === activeIndex;
      image.hidden = !active;
      image.setAttribute('aria-hidden', active ? 'false' : 'true');
      dots[index]?.toggleAttribute('data-active', active);
    }
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || !this.dialog.contains(event.target)) return;
    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;
        const newPrice = detail.html.querySelector('product-price [ref="priceContainer"]');
        const price = this.modalProduct.querySelector('[data-reel-price]');
        if (newPrice && price) price.replaceChildren(newPrice.cloneNode(true));

        const imageUrl = detail.resource?.featured_media?.preview_image?.src;
        const image = this.modalProduct.querySelector('.reel-product__image');
        if (imageUrl && image instanceof HTMLImageElement) {
          image.src = `${imageUrl}&width=360`;
          image.removeAttribute('srcset');
          const product = image.closest('[data-reel-product]');
          if (product) this.#showProductImage(product, 0);
        }
      })
      .catch(() => {});
  };

  #setInteracting(value) {
    this.#interacting = value;
    this.#syncPlayback();
  }

  #normalizeIndex(index) {
    return (index + this.cards.length) % this.cards.length;
  }

  #select(index, scroll = true) {
    this.#activeIndex = this.#normalizeIndex(index);

    for (const [cardIndex, card] of this.cards.entries()) {
      const active = cardIndex === this.#activeIndex;
      card.toggleAttribute('data-active', active);
      card.setAttribute('aria-current', active ? 'true' : 'false');
      if (!active) this.#removeCardVideo(card);
    }

    if (scroll) {
      this.cards[this.#activeIndex].scrollIntoView({
        behavior: this.#reducedMotion ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }

    this.#syncPlayback();
  }

  #syncPlayback = () => {
    const canPlay = this.#inViewport && !document.hidden && !this.dialog.open && !this.#reducedMotion;
    if (canPlay) this.#loadCardVideo(this.cards[this.#activeIndex]);
    else this.#removeAllCardVideos();

    this.#stopTimer();
    if (canPlay && this.dataset.autoplay === 'true' && !this.#interacting && this.cards.length > 1) {
      const seconds = Number(this.dataset.autoplaySpeed) || 5;
      this.#timer = window.setInterval(() => this.#select(this.#activeIndex + 1), seconds * 1000);
    }
  };

  #loadCardVideo(card) {
    const media = card.querySelector('[data-reel-card-media]');
    if (!media || media.querySelector('video')) return;
    const video = this.#cloneVideo(card);
    if (!video) return;
    video.muted = true;
    video.controls = false;
    video.setAttribute('aria-hidden', 'true');
    media.appendChild(video);
    video.play().catch(() => {});
  }

  #cloneVideo(card) {
    const template = card.querySelector('[data-reel-video-template]');
    const video = template?.content.firstElementChild?.cloneNode(true);
    return video instanceof HTMLVideoElement ? video : null;
  }

  #removeCardVideo(card) {
    const video = card.querySelector('[data-reel-card-media] > video');
    video?.pause();
    video?.remove();
  }

  #removeAllCardVideos() {
    for (const card of this.cards) this.#removeCardVideo(card);
  }

  #openDialog(index) {
    this.#previousFocus = document.activeElement;
    this.#modalIndex = this.#normalizeIndex(index);
    this.#removeAllCardVideos();
    this.#showModalReel(this.#modalIndex);
    document.documentElement.classList.add('reels-dialog-open');
    this.dialog.showModal();
    this.querySelector('[data-reel-close]')?.focus();
    this.#stopTimer();
  }

  #closeDialog() {
    if (!this.dialog.open) return;
    this.modalVideo.querySelector('video')?.pause();
    this.modalVideo.replaceChildren();
    this.modalProduct.replaceChildren();
    this.dialog.close();
    document.documentElement.classList.remove('reels-dialog-open');
    if (this.#previousFocus instanceof HTMLElement) this.#previousFocus.focus();
    this.#syncPlayback();
  }

  #showModalReel(index) {
    this.#modalIndex = this.#normalizeIndex(index);
    const card = this.cards[this.#modalIndex];
    const video = this.#cloneVideo(card);
    const productTemplate = card.querySelector('[data-reel-product-template]');

    this.modalVideo.querySelector('video')?.pause();
    this.modalVideo.replaceChildren();
    this.modalProduct.replaceChildren();

    if (video) {
      video.muted = this.#muted;
      video.controls = this.#reducedMotion;
      this.modalVideo.appendChild(video);
      if (!this.#reducedMotion) video.play().catch(() => {});
    }

    if (productTemplate?.content.firstElementChild) {
      this.modalProduct.appendChild(productTemplate.content.firstElementChild.cloneNode(true));
    }

    this.soundButton?.toggleAttribute('hidden', !video);
  }

  #toggleSound() {
    this.#muted = !this.#muted;
    const video = this.modalVideo.querySelector('video');
    if (video) video.muted = this.#muted;
    this.soundButton?.setAttribute('aria-pressed', this.#muted ? 'false' : 'true');
    this.soundButton?.setAttribute('aria-label', this.#muted ? this.soundButton.dataset.unmute : this.soundButton.dataset.mute);
  }

  #stopTimer() {
    window.clearInterval(this.#timer);
    this.#timer = undefined;
  }

  get #reducedMotion() {
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  }
}

if (!customElements.get('reel-video-carousel')) {
  customElements.define('reel-video-carousel', ReelVideoCarousel);
}
